import React, { useEffect } from 'react'
import { ReceiptIcon, TimeIcon, EmergencyIcon, GraphIcon } from '@instacart/ids-core'
import { colors, fontSize, spacing } from '@retailer-platform/shared-components'
import { PrimaryButton } from '@instacart/ids-tooling'
import styled from '@emotion/styled'
import { useIntl } from 'react-intl'
import welcomeModalHero from 'assets/pickup-welcome-modal-hero.png'
import Link from 'legacy/components/Link'
import { useDashMessages } from 'intl/intl.hooks'
import { useNavContext } from 'gin-and-tonic/containers/nav/utils/NavContext'
import {
  usePickupWelcomeTour,
  usePickupRemindMeLaterTour,
} from 'domains/pickup-settings/training-tour/pickupWelcomeTourSteps'
import { Status, useFeatureTourUpdateMutation } from 'domains/pickup-settings/api'
import {
  TrainingModal,
  TrainingModalFooter,
  TrainingModalContent,
  TrainingModalTitle,
} from './TrainingModalBase'

const Bold = styled.span({
  fontWeight: 600,
})

const featureBulletPoints = [
  {
    text: 'training.welcomeModal.content.feature1',
    icon: <ReceiptIcon />,
  },
  {
    text: 'training.welcomeModal.content.feature2',
    icon: <GraphIcon />,
  },
  {
    text: 'training.welcomeModal.content.feature3',
    icon: <TimeIcon />,
  },
  {
    text: 'training.welcomeModal.content.feature4',
    icon: <EmergencyIcon />,
  },
]

const StyledCancelButton = styled(PrimaryButton)({
  color: colors.GRAYSCALE.X70,
  backgroundColor: colors.GRAYSCALE.X10,
  fontSize: fontSize.X15,
  '&:hover': {
    backgroundColor: colors.GRAYSCALE.X20,
  },
})

const StyledConfirmButton = styled(PrimaryButton)({
  backgroundColor: colors.SUCCESS.REGULAR,
  fontSize: fontSize.X15,
  marginLeft: spacing.X8,
})

export function WelcomeModal({
  onClose,
  onConfirm,
  isOpen,
}: {
  onClose: () => void
  onConfirm: () => void
  maxWidth?: number
  isOpen: boolean
}) {
  const { startTour: startWelcomeTour } = usePickupWelcomeTour()
  const { startTour: startRemindMeLaterTour } = usePickupRemindMeLaterTour()
  const { setIsBlockedByModal } = useNavContext()
  const [setFeatureTourShown] = useFeatureTourUpdateMutation({
    variables: { name: 'pickup_overview', status: Status.Shown },
  })

  useEffect(() => {
    setIsBlockedByModal(isOpen)
  }, [isOpen, setIsBlockedByModal])

  const handleConfirm = () => {
    onConfirm()
    startWelcomeTour()
  }

  const handleClose = () => {
    onClose()
    startRemindMeLaterTour()
    setFeatureTourShown()
  }

  const intl = useIntl()
  const i18n = useDashMessages(
    {
      title: 'training.welcomeModal.title',
      intro: 'training.welcomeModal.content.intro',
      outro: 'training.welcomeModal.content.outro',
      remindMeLater: 'training.welcomeModal.button.remindMeLater',
      confirm: 'training.welcomeModal.button.confirm',
    },
    {
      iOS: (
        <Link href="https://apps.apple.com/us/app/instacart-shopper-earn-money/id1454056744">
          <Bold>iOS</Bold>
        </Link>
      ),
      Android: (
        <Link href="https://play.google.com/store/apps/details?id=com.instacart.shopper&hl=en_US&gl=US">
          <Bold>Android</Bold>
        </Link>
      ),
    }
  )
  return (
    <TrainingModal onClose={onClose} isOpen={isOpen} imgUrl={welcomeModalHero}>
      <TrainingModalTitle>{i18n.title}</TrainingModalTitle>
      <TrainingModalContent>
        {i18n.intro}
        <ul>
          {featureBulletPoints.map(bulletPoint => (
            <li
              key={bulletPoint.text}
              css={{
                padding: '2px',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {bulletPoint.icon}
              <span
                css={{
                  padding: '4px',
                }}
              >
                {intl.formatMessage({ id: bulletPoint.text })}
              </span>
            </li>
          ))}
        </ul>
        <p>{i18n.outro}</p>
      </TrainingModalContent>
      <TrainingModalFooter>
        <StyledCancelButton onClick={handleClose}>{i18n.remindMeLater}</StyledCancelButton>
        <StyledConfirmButton onClick={handleConfirm}>{i18n.confirm}</StyledConfirmButton>
      </TrainingModalFooter>
    </TrainingModal>
  )
}
